/**
 * Z3.3 — welche Mahnstufe ein Vorschlag der Freigabeliste trägt, und wie sie heißt.
 *
 * ## Warum das überhaupt eine eigene Datei ist
 *
 * Seit Z3.2 gibt es zwei Mahnstufen. In der Freigabeliste sehen beide gleich aus: dieselbe
 * Herkunft (`ueberfaellig_mahnung`), dieselbe Farbe, derselbe Empfänger, oft derselbe Vorgang.
 * Der Betreiber entscheidet damit über eine erste und eine letzte Mahnung, ohne den Unterschied
 * zu sehen — und die zweite ist die unangenehmere Entscheidung von beiden.
 *
 * ## Wo die Stufe herkommt — und warum sie NICHT gerechnet wird
 *
 * Aus dem Ablage-Schlüssel: `ueberfaellig_mahnung:<zahlung>:<stufe>` (sql/266, seit F1.5 A4). Er
 * steht in der Antwort von `GET /api/ausgaenge` und ist die einzige Wahrheit über die Stufe eines
 * VORSCHLAGS. Der Zähler `payments.mahnstufe` ist es ausdrücklich nicht: er sagt, wie weit die
 * Zahlung heute ist, nicht welche Stufe in dieser Zeile liegt — beim zweiten Vorschlag stünden
 * beide Zeilen auf 2.
 *
 * Gelesen, nicht abgeleitet: die Anzeige zählt nicht mit, vergleicht nichts und rät nichts.
 * Passt der Schlüssel nicht auf das Muster, liefert sie `null`, und die Zeile bleibt bei ihrer
 * unveränderten Beschriftung. Eine erfundene Stufe wäre schlimmer als keine — der Betreiber
 * entschiede über eine Mahnung, die er für die erste hält.
 */

/** Wie viele Stufen es gibt (GATE-Z3, dieselbe Grenze wie der CHECK in sql/273). */
export const MAHNSTUFEN_GESAMT = 2;

/**
 * Die Stufe aus dem Ablage-Schlüssel — oder `null`, wenn dort keine steht.
 *
 * Bewusst streng: nur `ueberfaellig_mahnung:<irgendetwas>:<ziffern>` zählt, und nur ein Wert
 * innerhalb der beschlossenen Stufen. Eine „Stufe 7" aus einem verunglückten Schlüssel wäre eine
 * Auskunft, die niemand prüfen kann.
 */
export function mahnstufeAusSchluessel(schluessel: string | null | undefined): number | null {
  if (!schluessel) return null;
  const treffer = /^ueberfaellig_mahnung:.+:(\d+)$/.exec(schluessel);
  if (!treffer) return null;
  const stufe = Number(treffer[1]);
  if (!Number.isInteger(stufe) || stufe < 1 || stufe > MAHNSTUFEN_GESAMT) return null;
  return stufe;
}

/**
 * Der Zusatz hinter „Mahnung wegen Überfälligkeit" — oder `null`, wenn es nichts zu sagen gibt.
 *
 * Die letzte Stufe sagt ausdrücklich, dass sie die letzte ist. Das ist keine Verzierung: danach
 * kommt keine dritte Mail mehr, sondern eine Aufgabe im Backoffice (GATE-Z3). Wer das nicht
 * weiß, verwirft die zweite Mahnung in der Annahme, es käme ja noch eine.
 */
export function mahnstufeText(schluessel: string | null | undefined): string | null {
  const stufe = mahnstufeAusSchluessel(schluessel);
  if (stufe === null) return null;
  return stufe >= MAHNSTUFEN_GESAMT ? `${stufe}. und letzte Stufe` : `${stufe}. Stufe`;
}
